import { type SubscriptionFormData } from "./formDataHelpers";
import { type Subscription } from "@/types/subscription";

interface EnhancedDebugPayload {
  instructions: {
    purpose: string;
    action: string;
    suggestedPrompts: string[];
    dataStructure: {
      context: string;
      formState: string;
      subscription: string;
      relatedData: string;
      userSettings: string;
      viewingContext: string;
      databaseMappings: string;
    };
  };
  context: {
    action: "creating" | "editing";
    timestamp: string;
    component: string;
    subscriptionId?: string;
    subscriptionName?: string;
  };
  formState: {
    formData: SubscriptionFormData;
    validationErrors?: any;
    startDateValidation?: any;
    isDirty: boolean;
  };
  subscription: {
    current?: Subscription;
    tags?: {
      ids: string[];
      names: string[];
    };
    summary?: {
      activeFor?: string;
      totalPayments: number;
      totalSpent: number;
      lastPaymentDate?: string;
      nextPaymentDate?: string;
    };
  };
  relatedData: {
    paymentHistory?: any[];
    lifecycleEvents?: any[];
    subscriptionPeriods?: any[];
    categories: Array<{ id: string; name: string; color?: string }>;
  };
  userSettings?: {
    displayCurrency?: string;
    showOriginalCurrency?: boolean;
    preferredRateSource?: string;
    theme?: string;
  };
  viewingContext?: {
    currentPeriod?: { month: number; year: number };
    currentRoute?: string;
  };
  databaseMappings: {
    originalSubscriptionAsDb?: any;
    formDataAsDb?: any;
    transformationErrors: string[];
  };
  hasErrors: boolean;
  errors?: {
    validation?: string[];
    transformation?: string[];
  };
}

interface DebugDependencies {
  subscription?: Subscription;
  formData: SubscriptionFormData;
  tagIds: string[];
  categories: any[];
  payments: any[];
  lifecycleEvents: any[];
  subscriptionPeriods: any[];
  transformToDatabase: (data: any) => any;
  userSettings?: {
    displayCurrency?: string;
    showOriginalCurrency?: boolean;
    preferredRateSource?: string;
    theme?: string;
  };
  currentPeriod?: { month: number; year: number };
  startDateValidation?: any;
  isDirty?: boolean;
  include?: DebugCopyOptions;
}

export interface DebugCopyOptions {
  payments: boolean;
  lifecycleEvents: boolean;
  subscriptionPeriods: boolean;
  tags: boolean;
}

export const handleDebugCopy = async ({
  subscription,
  formData,
  tagIds,
  categories,
  payments,
  lifecycleEvents,
  subscriptionPeriods,
  transformToDatabase,
  userSettings,
  currentPeriod,
  startDateValidation,
  isDirty = false,
  include = {
    payments: true,
    lifecycleEvents: true,
    subscriptionPeriods: true,
    tags: true,
  },
}: DebugDependencies): Promise<{ success: boolean; error?: string }> => {
  const isEditing = !!subscription?.id;

  try {
    const tagNames = tagIds
      .map((id) => categories.find((c: any) => c.id === id)?.name)
      .filter(Boolean) as string[];

    // Filter related data by subscription ID
    const subId = subscription?.id;
    const filteredPayments = subId
      ? payments.filter((p: any) => p.subscription_id === subId)
      : [];
    const filteredLifecycleEvents = subId
      ? lifecycleEvents.filter((e: any) => e.subscription_id === subId)
      : [];
    const filteredSubscriptionPeriods = subId
      ? subscriptionPeriods.filter((sp: any) => sp.subscription_id === subId)
      : [];

    // Calculate summary statistics
    const paidPayments = filteredPayments.filter(
      (p: any) => p.status === "paid",
    );
    const totalSpent = paidPayments.reduce(
      (sum: number, p: any) => sum + (p.amount || 0),
      0,
    );
    const lastPayment = paidPayments.sort(
      (a: any, b: any) =>
        new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime(),
    )[0];

    let activeFor: string | undefined;
    if (subscription?.startDate) {
      const start = new Date(subscription.startDate);
      const now = new Date();
      const months = Math.floor(
        (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30),
      );
      activeFor = `${months} month${months !== 1 ? "s" : ""}`;
    }

    // Attempt database transformations and capture errors
    const transformationErrors: string[] = [];
    let dbMappingFromProp: any = null;
    let dbMappingFromForm: any = null;

    if (subscription) {
      try {
        dbMappingFromProp = transformToDatabase(subscription);
      } catch (error) {
        transformationErrors.push(
          `Failed to transform original subscription: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    }

    try {
      // Create a subscription-like object from form data for transformation testing
      const formAsSubscription: Partial<Subscription> = {
        name: formData.name,
        amount: Number(formData.amount),
        currency: formData.currency,
        frequency: formData.frequency as Subscription["frequency"],
        customFrequencyDays:
          formData.frequency === "custom"
            ? formData.customFrequencyDays
            : undefined,
        nextPayment: new Date(formData.nextPayment),
        startDate: new Date(formData.startDate),
        status: formData.status as Subscription["status"],
        category: tagNames[0] || subscription?.category || "Uncategorized",
        notes: formData.notes,
        sentiment: formData.sentiment,
        cancellationDate:
          formData.status === "cancelled" && formData.cancellationDate
            ? new Date(formData.cancellationDate)
            : undefined,
      };
      dbMappingFromForm = transformToDatabase(formAsSubscription);
    } catch (error) {
      transformationErrors.push(
        `Failed to transform form data: ${error instanceof Error ? error.message : String(error)}`,
      );
    }

    // Collect validation errors
    const validationErrors: string[] = [];
    if (startDateValidation) {
      if (startDateValidation.hasWarning) {
        validationErrors.push(
          startDateValidation.warning || "Start date validation warning",
        );
      }
      if (startDateValidation.hasError) {
        validationErrors.push(
          startDateValidation.error || "Start date validation error",
        );
      }
    }

    const hasErrors =
      validationErrors.length > 0 || transformationErrors.length > 0;

    // Build enhanced debug payload
    const payload: EnhancedDebugPayload = {
      instructions: {
        purpose: isEditing
          ? `Debug/enhance subscription: ${formData.name} (${formData.currency} ${formData.amount}/${formData.frequency})`
          : `Creating new subscription: ${formData.name}`,
        action: isEditing
          ? "User is editing an existing subscription"
          : "User is creating a new subscription",
        suggestedPrompts: isEditing
          ? [
              "Review payment history and verify billing cycle accuracy",
              "Check if next payment date aligns with payment history",
              "Identify any gaps or issues in the subscription lifecycle",
              "Analyze the subscription's reconciliation status and payment patterns",
              "Suggest improvements based on the subscription data",
            ]
          : [
              "Review the subscription form data for any potential issues",
              "Validate the billing cycle and payment schedule",
              "Check if the start date and next payment date are correctly configured",
            ],
        dataStructure: {
          context: "Information about what action is being performed",
          formState: "Current form data and validation state",
          subscription:
            "The original subscription data from database (if editing)",
          relatedData:
            "All payments, events, and periods for this subscription",
          userSettings: "User preferences affecting display and calculations",
          viewingContext:
            "Current month/year being viewed and navigation state",
          databaseMappings: "Database transformation attempts and any errors",
        },
      },
      context: {
        action: isEditing ? "editing" : "creating",
        timestamp: new Date().toISOString(),
        component: "AddSubscriptionDialog",
        subscriptionId: subscription?.id,
        subscriptionName: formData.name,
      },
      formState: {
        formData,
        validationErrors:
          validationErrors.length > 0 ? validationErrors : undefined,
        startDateValidation,
        isDirty,
      },
      subscription: {
        current: subscription,
        tags: include.tags ? { ids: tagIds, names: tagNames } : undefined,
        summary: subscription
          ? {
              activeFor,
              totalPayments: paidPayments.length,
              totalSpent,
              lastPaymentDate: lastPayment?.payment_date,
              nextPaymentDate:
                typeof subscription.nextPayment === "string"
                  ? subscription.nextPayment
                  : subscription.nextPayment?.toISOString?.(),
            }
          : undefined,
      },
      relatedData: {
        paymentHistory: include.payments ? filteredPayments : undefined,
        lifecycleEvents: include.lifecycleEvents
          ? filteredLifecycleEvents
          : undefined,
        subscriptionPeriods: include.subscriptionPeriods
          ? filteredSubscriptionPeriods
          : undefined,
        categories: categories.map((c: any) => ({
          id: c.id,
          name: c.name,
          color: c.color,
        })),
      },
      userSettings,
      viewingContext: currentPeriod
        ? {
            currentPeriod,
            currentRoute: window.location.pathname,
          }
        : undefined,
      databaseMappings: {
        originalSubscriptionAsDb: dbMappingFromProp,
        formDataAsDb: dbMappingFromForm,
        transformationErrors,
      },
      hasErrors,
      errors: hasErrors
        ? {
            validation:
              validationErrors.length > 0 ? validationErrors : undefined,
            transformation:
              transformationErrors.length > 0
                ? transformationErrors
                : undefined,
          }
        : undefined,
    };

    const json = JSON.stringify(payload, null, 2);
    await navigator.clipboard.writeText(json);

    return { success: true };
  } catch (error) {
    console.error("Failed to copy debug data:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};
